import { useState } from "react";
import api, { apiError } from "@/lib/crmApi";
import { toast } from "sonner";
import {
  Globe, ShieldCheck, ShieldQuestion, ScanSearch, RefreshCw, ShoppingCart,
  Gauge, Search, Lightbulb,
} from "lucide-react";

function Bar({ label, value }) {
  const v = Number(value) || 0;
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-slate-500 w-24 shrink-0">{label}</span>
      <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full bg-indigo-500" style={{ width: `${v}%` }} />
      </div>
      <span className="text-xs font-mono text-slate-600 w-10 text-right">{v}</span>
    </div>
  );
}

export default function SiteCard({ company, onChanged }) {
  const [loading, setLoading] = useState(false);
  const a = company.site_analysis;

  const validate = async (validated) => {
    try {
      await api.post(`/companies/${company.id}/site/validate`, { validated });
      toast.success(validated ? "Site validé" : "Site invalidé");
      onChanged?.();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const analyze = async () => {
    setLoading(true);
    try {
      await api.post(`/companies/${company.id}/site/analyze`);
      toast.success("Analyse terminée");
      onChanged?.();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="site-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading font-semibold text-slate-800 flex items-center gap-2"><Globe size={16} /> Site internet</h3>
        {company.site_validated
          ? <span data-testid="site-status" className="inline-flex items-center gap-1 text-xs bg-emerald-100 text-emerald-700 rounded-md px-2 py-1"><ShieldCheck size={12} /> Validé</span>
          : <span data-testid="site-status" className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 rounded-md px-2 py-1"><ShieldQuestion size={12} /> Non validé</span>}
      </div>

      {!company.site_web ? (
        <p className="text-sm text-slate-400">Aucun site renseigné</p>
      ) : (
        <>
          <a href={company.site_web.startsWith("http") ? company.site_web : `https://${company.site_web}`} target="_blank" rel="noreferrer"
            className="text-sm text-indigo-600 hover:underline break-all">{company.site_web}</a>

          <div className="flex flex-wrap gap-2 mt-3">
            {!company.site_validated ? (
              <button data-testid="site-validate-btn" onClick={() => validate(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs hover:bg-emerald-700"><ShieldCheck size={13} /> Valider le site</button>
            ) : (
              <>
                <button data-testid="site-analyze-btn" onClick={analyze} disabled={loading} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs hover:bg-indigo-700 disabled:opacity-50">
                  {loading ? <RefreshCw size={13} className="animate-spin" /> : <ScanSearch size={13} />} {a ? "Ré-analyser" : "Analyser le site"}
                </button>
                <button data-testid="site-unvalidate-btn" onClick={() => validate(false)} className="px-3 py-1.5 rounded-lg border border-slate-300 text-xs hover:bg-slate-50">Invalider</button>
              </>
            )}
          </div>
          {!company.site_validated && <p className="text-xs text-slate-400 mt-2">Validez le site pour activer l'analyse IA.</p>}

          {a && (
            <div className="mt-4 space-y-4 border-t border-slate-100 pt-4" data-testid="site-analysis">
              {a.type_site && (
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1 text-xs bg-indigo-100 text-indigo-700 rounded-md px-2 py-1"><ShoppingCart size={12} /> {a.type_site}</span>
                </div>
              )}
              {a.resume && <div><p className="text-xs font-medium text-slate-500 mb-1">Activité</p><p className="text-sm text-slate-700">{a.resume}</p></div>}

              {(a.produits?.length || a.services?.length) ? (
                <div className="grid grid-cols-2 gap-3">
                  {a.produits?.length > 0 && <div><p className="text-xs font-medium text-slate-500 mb-1">Produits</p><ul className="text-sm text-slate-700 list-disc pl-4 space-y-0.5">{a.produits.slice(0, 6).map((p, i) => <li key={i}>{p}</li>)}</ul></div>}
                  {a.services?.length > 0 && <div><p className="text-xs font-medium text-slate-500 mb-1">Services</p><ul className="text-sm text-slate-700 list-disc pl-4 space-y-0.5">{a.services.slice(0, 6).map((s, i) => <li key={i}>{s}</li>)}</ul></div>}
                </div>
              ) : null}

              {a.uiux && Object.keys(a.uiux).length > 0 && (
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-2 flex items-center gap-1"><Gauge size={12} /> UI / UX</p>
                  <div className="space-y-1.5">
                    <Bar label="Global" value={a.uiux.score} />
                    <Bar label="Design" value={a.uiux.design} />
                    <Bar label="Mobile" value={a.uiux.mobile} />
                    <Bar label="Navigation" value={a.uiux.navigation} />
                    <Bar label="Modernité" value={a.uiux.modernite} />
                  </div>
                  {a.uiux.raisons?.length > 0 && <ul className="text-xs text-slate-500 list-disc pl-4 mt-2 space-y-0.5">{a.uiux.raisons.slice(0, 4).map((r, i) => <li key={i}>{r}</li>)}</ul>}
                </div>
              )}

              {a.seo && Object.keys(a.seo).length > 0 && (
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-2 flex items-center gap-1"><Search size={12} /> SEO apparent (indicatif)</p>
                  <Bar label="Score" value={a.seo.score} />
                  <div className="text-xs text-slate-500 mt-1">Présence : {a.seo.presence_web || "—"} · Contenu : {a.seo.contenu || "—"} · Structure : {a.seo.structure || "—"}</div>
                </div>
              )}

              {a.opportunites?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-1 flex items-center gap-1"><Lightbulb size={12} /> Opportunités commerciales</p>
                  <ul className="text-sm text-amber-700 list-disc pl-4 space-y-0.5">{a.opportunites.map((o, i) => <li key={i}>{o}</li>)}</ul>
                </div>
              )}
              {a.analyzed_at && <p className="text-xs text-slate-400">Analysé le {new Date(a.analyzed_at).toLocaleString("fr-FR")}</p>}
            </div>
          )}
        </>
      )}
    </div>
  );
}
